import { useEffect, useRef } from "react"

const SWIPE_THRESHOLD_PX = 50

// Not a native DOM event - tracks touchstart/touchend on the returned ref's element and
// calls onSwipe once a horizontal swipe crosses SWIPE_THRESHOLD_PX.
const useOnSwipe = <T extends HTMLElement>(onSwipe: (direction: "left" | "right") => void) => {
  const ref = useRef<T>(null)
  const onSwipeRef = useRef(onSwipe)
  onSwipeRef.current = onSwipe

  useEffect(() => {
    const el = ref.current
    if (!el) { return }

    let startX: number | null = null

    const onTouchStart = (evt: TouchEvent) => {
      startX = evt.touches[0]?.clientX ?? null
    }

    const onTouchEnd = (evt: TouchEvent) => {
      if (startX === null) { return }
      const endX = evt.changedTouches[0]?.clientX
      if (endX !== undefined) {
        const deltaX = endX - startX
        if (Math.abs(deltaX) >= SWIPE_THRESHOLD_PX) {
          onSwipeRef.current(deltaX > 0 ? "right" : "left")
        }
      }
      startX = null
    }

    el.addEventListener("touchstart", onTouchStart)
    el.addEventListener("touchend", onTouchEnd)

    return () => { // cleanup
      el.removeEventListener("touchstart", onTouchStart)
      el.removeEventListener("touchend", onTouchEnd)
    }
  }, [])

  return ref
}

export default useOnSwipe
